import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Settings, SettingsTextField } from 'components/SettingsNew';
import {
  setField,
  startEdit,
  cancelEdit,
  finishEdit,
} from 'actions/matrixSettings';
import { useHistory } from 'react-router';
import * as urls from 'constants/urls';
import MatrixDeletePopoverWrapper from 'components/matrices/MatrixDeletePopoverWrapper';
import { canDelete } from 'constants/matrices';
import { saveMatrix } from 'actions/matrices';
import './MatrixSettings.scss';
import ButtonNew from 'components/inputs/ButtonNew';
import MatrixSettingsBasic from './MatrixSettingsBasic';
import MatrixSettingsAdvanced from './MatrixSettingsAdvanced';
import SettingsModal from 'components/SettingsModal';
import Popover from '../../Popover';
import ModalFooter from '../../ModalFooter';
import { useCanEdit, useIsInUse } from 'hooks/matrices';
import { writePermissions } from 'constants/auth';

const MatrixFooter = () => {
  return (
    <div>
      Not sure which settings to choose? Learn more about how the MPG matrix
      works in our{' '}
      <a
        href="https://docs.google.com/document/d/1HGGWkx9PZrsdBR3CNIeZvDfOc0IhHvV7jFi56v4PGjg/edit"
        target="_blank"
      >
        FAQs
      </a>
    </div>
  );
};

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const MatrixSettings = ({
  className,
  matrix,
  base,
  saveUrl,
  cancelUrl,
  publicWarningDismissed,
  showPublicWarning,
  setShowPublicWarning,
}) => {
  const prevShowPublicWarning = usePrevious(showPublicWarning);

  const dispatch = useDispatch();
  const history = useHistory();
  const editingMatrix = useSelector(state => state.matrixSettings.matrix);

  const { isAdmin } = useSelector(state => state.auth.userData);

  const { user, userData } = useSelector(state => state.auth);

  const isDeletable = canDelete(editingMatrix, user, userData);

  const { user_id } = user?.claims ?? { user_id: null };
  const canEdit = useCanEdit();

  const isAnonymous = useMemo(() => {
    return (
      !user &&
      matrix?.createdBy === 'anonymous user' &&
      Object.keys(matrix?.roles)?.length === 0
    );
  }, [matrix, user]);

  const hasEditAccess = useMemo(() => {
    return isAnonymous || writePermissions.includes(matrix?.roles?.[user_id]);
  }, [matrix, user_id, isAnonymous]);
  const isInUse = useIsInUse({ id: matrix?.id, hasEditAccess });

  const matrixIsInUse = useMemo(() => {
    return (
      hasEditAccess &&
      !!isInUse &&
      isInUse?.user !== user_id &&
      isInUse?.user !== null
    );
  }, [user_id, isInUse, hasEditAccess]);

  const editable = useMemo(() => {
    // TODO This will require more nuance because they need to edit the matrix to choose this
    const isViewOnly = matrix?.isViewOnly;
    return isAnonymous || (canEdit(matrix) && !matrixIsInUse && !isViewOnly);
  }, [matrixIsInUse, matrix, canEdit, isAnonymous]);

  const noEdit = useMemo(() => {
    return matrixIsInUse || !hasEditAccess || !editable;
  }, [matrixIsInUse, hasEditAccess, editable]);

  const matrixIsPublic = useMemo(() => {
    return matrix?.isPublic ?? false;
  }, [matrix]);

  useEffect(() => {
    if (matrix) {
      dispatch(startEdit(matrix));
    }
  }, [dispatch, matrix]);

  const { valid: matrixIsValid } = useSelector(state => state.matrixSettings);

  const onSave = useCallback(() => {
    editingMatrix.savedByUser = true;
    dispatch(finishEdit(editingMatrix));
    if (matrixIsValid) {
      dispatch(saveMatrix(editingMatrix.id));
    }
    history.push(saveUrl);
  }, [editingMatrix, history, matrixIsValid, dispatch, saveUrl]);

  const onCancel = useCallback(() => {
    dispatch(cancelEdit());
    history.push(cancelUrl);
  }, [history, dispatch, cancelUrl]);

  const { validationMessages } = useSelector(state => state.matrixSettings);

  let [isEditing, setIsEditing] = useState(false);

  const [settingsState, setSettingsState] = useState('basic');

  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);

  const menuOptions = useMemo(() => {
    let options = [
      {
        label: 'Basic',
        value: 'basic',
        content: (
          <MatrixSettingsBasic
            matrix={editingMatrix}
            base={base}
            isEditing={isEditing}
          />
        ),
      },
      {
        label: 'Advanced',
        value: 'advanced',
        content: (
          <MatrixSettingsAdvanced
            matrix={editingMatrix}
            isEditing={isEditing}
          />
        ),
      },
    ];

    return options;
  }, [editingMatrix, base, isEditing]);

  const deleteRef = useRef(null);

  const onDelete = useCallback(() => {
    dispatch(cancelEdit());
    history.push(urls.gallery());
  }, [history, dispatch]);

  const tertiaryAction = useCallback(() => {
    if (settingsState === 'basic') {
      setSettingsState('advanced');
    } else {
      setSettingsState('basic');
    }
  }, [settingsState]);

  const tertiaryActionLabel = useMemo(() => {
    if (settingsState === 'basic') {
      return 'Advanced settings';
    } else {
      return 'Go back';
    }
  }, [settingsState]);

  useEffect(() => {
    if (
      prevShowPublicWarning &&
      !showPublicWarning &&
      (!noEdit || isAdmin) &&
      !matrixIsInUse
    ) {
      setIsEditing(true);
    }
  }, [
    showPublicWarning,
    prevShowPublicWarning,
    noEdit,
    isAdmin,
    matrixIsInUse,
  ]);

  if (!editingMatrix) return null;

  return (
    <SettingsModal
      className="MatrixSettingsView"
      onExit={noEdit ? onCancel : onSave}
    >
      <div className="MatrixSettings">
        <div className="MatrixSettings-main">
          <div className="MatrixSettings-row">
            {isEditing ? (
              <SettingsTextField
                onChange={value => dispatch(setField('name', value))}
                placeholder="Enter a name"
                validationMessage={validationMessages.name}
                value={editingMatrix.name}
              />
            ) : (
              <div className="MatrixSettings-name">{editingMatrix.name}</div>
            )}

            {/* Edit */}
            {!noEdit || publicWarningDismissed ? (
              <div className="NodeSettings-button-container">
                <ButtonNew
                  buttonClasses={classnames(
                    'settings-action-button NodeSettings-edit-button',
                    { active: isEditing }
                  )}
                  onClick={() => {
                    if (
                      matrixIsPublic &&
                      !publicWarningDismissed &&
                      !isEditing
                    ) {
                      setShowPublicWarning(true);
                    } else {
                      setIsEditing(!isEditing);
                    }
                  }}
                  iconLeft="pencil"
                  label={isEditing ? 'Editing matrix' : 'Edit matrix'}
                />
              </div>
            ) : (
              <div className="NodeSettings-button-container">
                <ButtonNew
                  buttonClasses={classnames(
                    'settings-action-button NodeSettings-edit-button',
                    { active: isEditing }
                  )}
                  onClick={() => {
                    if (!isEditing) {
                      setShowPublicWarning(true);
                    }
                  }}
                  iconLeft="pencil"
                  label={'Edit matrix'}
                />
              </div>
            )}

            {/* Delete */}
            {/* Not all matrices can be deleted */}
            {isDeletable && isEditing ? (
              <div ref={deleteRef} className="MatrixSettings-button-container">
                <ButtonNew
                  buttonClasses="settings-action-button MatrixSettings-delete-button"
                  onClick={() => setDeletePopoverOpen(!deletePopoverOpen)}
                  iconLeft="trash"
                  label="Delete matrix"
                />
              </div>
            ) : null}
          </div>

          <Settings
            menuOptions={menuOptions}
            setMenuOption={setSettingsState}
            activeMenuOption={settingsState}
            footer={<MatrixFooter />}
          />
        </div>

        {/* Footer */}
        <div className="MatrixSettings-footer">
          <ModalFooter
            secondaryAction={onCancel}
            secondaryActionLabel={'Cancel'}
            primaryAction={onSave}
            primaryActionDisabled={!matrixIsValid || (noEdit && !isEditing)}
            primaryActionLabel={'OK'}
            tertiaryAction={tertiaryAction}
            tertiaryActionLabel={tertiaryActionLabel}
          />
        </div>

        {/* Delete popover */}
        <Popover
          title={'Delete matrix'}
          content={
            <MatrixDeletePopoverWrapper
              matrix={editingMatrix}
              onDelete={() => {
                setDeletePopoverOpen(false);
                onDelete();
              }}
              onCancel={() => setDeletePopoverOpen(false)}
            />
          }
          isOpen={deletePopoverOpen}
          parentRef={deleteRef.current}
        />
      </div>
    </SettingsModal>
  );
};

export default MatrixSettings;
